import {Select} from '@wandb/weave/components/Form/Select';
import React, {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import {toast} from 'react-toastify';

import {useUpdateMemberMutation} from '../../../generated/graphql';
import {MemberData, TeamBaseEntity} from '../../../graphql/teamQuery';
import {
  extractErrorMessageFromApolloError,
  getErrMsgFromError,
  propagateErrorsContext,
} from '../../../util/errors';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {
  customSelectComponentForRoleOptions,
  RoleOption,
  TeamOrOrganizationRoleOption,
} from '../TeamOrOrganizationRoleOption';

export interface TeamMembersRoleProps {
  roleOptions: RoleOption[];
  canViewerPerformActions: boolean;
  entity: TeamBaseEntity;
  member: MemberData;
  refetch?: () => void;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export const TeamMembersRole: FC<TeamMembersRoleProps> = memo(
  ({
    roleOptions,
    canViewerPerformActions,
    entity,
    member,
    refetch,
    setIsLoading,
  }) => {
    const role = roleOptions.find(r => r.value === member.memberRole.name);
    const [isOpen, setIsOpen] = useState(false);

    const [updateMemberMutation] = useUpdateMemberMutation({
      context: propagateErrorsContext(),
    });

    const handleChangeMemberRole = useCallback(
      async (newRole: RoleOption) => {
        if (setIsLoading) {
          setIsLoading(true);
        }
        try {
          const result = await updateMemberMutation({
            variables: {
              user: member.id,
              role: newRole.value,
              entityName: entity?.name ?? '',
            },
          });

          if (result.errors != null) {
            toast(
              result.errors.map((e: {message: string}) => e.message).join(', ')
            );
            if (setIsLoading) {
              setIsLoading(false);
            }
            console.error(extractErrorMessageFromApolloError(result.errors));
            return;
          }
          if (refetch != null) {
            await refetch();
          }
          if (setIsLoading) {
            setIsLoading(false);
          }
          window.analytics?.track('Team Member Role Changed', {
            location: 'team settings page',
            userID: member.id,
            newRole: newRole.value,
            entityName: entity?.name ?? '',
          });
        } catch (err) {
          const errMsg = getErrMsgFromError(err);
          toast(errMsg);
          if (setIsLoading) {
            setIsLoading(false);
          }
          if (errMsg != null) {
            console.error(errMsg);
          }
          return;
        }
      },
      [updateMemberMutation, member.id, entity, refetch, setIsLoading]
    );

    if (member.pending) {
      return (
        <div
          data-test="team-member-role"
          className="flex overflow-visible pl-5 text-moon-450">
          Invited
        </div>
      );
    }

    if (checkIsServiceAccount(member.accountType)) {
      return (
        <span
          className="flex overflow-visible pl-5 text-moon-450"
          data-test="team-member-role">
          Service
        </span>
      );
    }

    return canViewerPerformActions ? (
      <div data-test="team-member-role">
        <Select<RoleOption, false>
          menuIsOpen={isOpen}
          onMenuOpen={() => setIsOpen(true)}
          onMenuClose={() => setIsOpen(false)}
          options={roleOptions}
          formatOptionLabel={option => (
            <TeamOrOrganizationRoleOption roleOption={option} />
          )}
          value={roleOptions.find(option => option.text === role?.text)}
          onChange={selected => {
            if (selected?.value && !selected?.isDisabled) {
              handleChangeMemberRole(selected);
            }
          }}
          components={customSelectComponentForRoleOptions()}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          isSearchable={false}
          isLoading={false}
          classNamePrefix="team-member-role-dropdown-trigger"
        />
      </div>
    ) : (
      <span className="flex pl-5" data-test="team-member-role">
        {role?.text}
      </span>
    );
  }
);
